import { Badge, Box, HStack, Icon, IconButton, Text, VStack } from "@chakra-ui/react";
import { HiArrowDownTray, HiOutlineClipboard } from "react-icons/hi2";

/** Shared building blocks for the admin detail pages (client / recruitment / campaign / interaction). */

export const tableHeaderStyle = {
    color: "text.grey",
    fontWeight: 600,
    fontSize: "xs",
    textTransform: "uppercase",
    letterSpacing: "wider",
    py: 3,
    px: 5,
    border: "none",
    whiteSpace: "nowrap",
};

export const InfoField = ({ label, children, mono = false }) => (
    <VStack align="flex-start" gap={1} minW="0">
        <Text color="text.grey" fontSize="xs" fontWeight={600} textTransform="uppercase" letterSpacing="wider">
            {label}
        </Text>
        {typeof children === "string" || typeof children === "number" ? (
            <Text color="white" fontSize="sm" fontFamily={mono ? "mono" : undefined}>
                {children === "" || children == null ? "—" : children}
            </Text>
        ) : (
            children ?? <Text color="text.grey" fontSize="sm">—</Text>
        )}
    </VStack>
);

export const SectionCard = ({ icon, title, action, children, contentProps }) => (
    <Box w="full" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
        {(title || action) && (
            <HStack mb={4} gap={3} align="center" justify="space-between">
                <HStack gap={3} align="center">
                    {icon && (
                        <Box bg="bg.dark" borderRadius={8} p={2} color="text.grey" display="flex">
                            <Icon fontSize={18}>{icon}</Icon>
                        </Box>
                    )}
                    {title && <Text color="white" fontWeight={700} fontSize="md">{title}</Text>}
                </HStack>
                {action}
            </HStack>
        )}
        <Box {...contentProps}>{children}</Box>
    </Box>
);

const TYPE_META = {
    Recruiter: { label: "Rekruter", palette: "blue" },
    SalesManager: { label: "Sales Manager", palette: "purple" },
};

export const ProductLineBadge = ({ type }) => {
    const meta = TYPE_META[type];
    if (!meta) return <Text color="text.grey" fontSize="sm">—</Text>;
    return (
        <Badge colorPalette={meta.palette} variant="subtle" fontSize="xs" px={2} borderRadius={6}>
            {meta.label}
        </Badge>
    );
};

const CHANNEL_META = {
    WhatsApp: { label: "WhatsApp", palette: "green" },
    Email: { label: "E-mail", palette: "blue" },
    Chat: { label: "Chat", palette: "purple" },
};

export const ChannelBadge = ({ channel }) => {
    const meta = CHANNEL_META[channel] ?? CHANNEL_META.Chat;
    return (
        <Badge colorPalette={meta.palette} variant="subtle" fontSize="xs" px={2} borderRadius={6}>
            {meta.label}
        </Badge>
    );
};

const WORKFLOW_STATUS_META = {
    active: { label: "Aktywna", palette: "green" },
    inactive: { label: "Nieaktywna", palette: "gray" },
    draft: { label: "Szkic", palette: "orange" },
};

export const WorkflowStatusBadge = ({ status }) => {
    const meta = WORKFLOW_STATUS_META[String(status ?? "").toLowerCase()] ?? { label: status ?? "—", palette: "gray" };
    return (
        <Badge colorPalette={meta.palette} variant="solid" fontSize="xs" px={3} py={0.5} borderRadius={6}>
            {meta.label}
        </Badge>
    );
};

const INTERACTION_STATUS_META = {
    inprogress: { label: "W toku", palette: "blue" },
    finished: { label: "Zakończona", palette: "green" },
    cancelled: { label: "Anulowana", palette: "red" },
    holded: { label: "Wstrzymana", palette: "orange" },
};

export const InteractionStatusBadge = ({ status }) => {
    if (!status) return <Text color="text.grey" fontSize="sm">—</Text>;
    const meta = INTERACTION_STATUS_META[String(status).toLowerCase()] ?? { label: status, palette: "gray" };
    return (
        <Badge colorPalette={meta.palette} variant="subtle" fontSize="xs" px={2} borderRadius={6}>
            {meta.label}
        </Badge>
    );
};

const parseJson = (value) => {
    if (value == null) return null;
    if (Array.isArray(value) && value.length === 0) return null;
    try {
        return typeof value === "string" ? JSON.parse(value) : value;
    } catch {
        return null;
    }
};

const triggerBlobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

export const JsonSection = ({ title, data, downloadFilename }) => {
    const parsed = parseJson(data);
    const json = parsed ? JSON.stringify(parsed, null, 2) : null;

    const handleCopy = () => json && navigator.clipboard.writeText(json);
    const handleDownload = () =>
        json && triggerBlobDownload(new Blob([json], { type: "application/json" }), downloadFilename);

    return (
        <VStack w="full" align="flex-start" gap={3}>
            <HStack w="full" justify="space-between">
                <Text fontWeight={700} fontSize="lg" color="white">{title}</Text>
                {json && (
                    <HStack gap={2}>
                        <IconButton variant="ghost" size="sm" aria-label="Kopiuj JSON" onClick={handleCopy} color="text.grey" _hover={{ color: "white" }}>
                            <HiOutlineClipboard />
                        </IconButton>
                        <IconButton variant="ghost" size="sm" aria-label="Pobierz JSON" onClick={handleDownload} color="text.grey" _hover={{ color: "white" }}>
                            <HiArrowDownTray />
                        </IconButton>
                    </HStack>
                )}
            </HStack>
            <Box w="full" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5} overflow="hidden">
                {json ? (
                    <Box as="pre" bg="bg.secondary" borderRadius={8} overflow="auto" p={4} whiteSpace="break-spaces" maxH="400px" fontSize="sm" fontFamily="mono" color="white">
                        {json}
                    </Box>
                ) : (
                    <Text color="text.grey" fontSize="sm">Brak danych</Text>
                )}
            </Box>
        </VStack>
    );
};

/** Card-wrapped table with a shared header style and an empty-state row. */
export const DetailTableShell = ({ children }) => (
    <Box w="full" border="1px solid" borderColor="border.primary" borderRadius={12} overflow="hidden">
        <Box overflowX="auto">{children}</Box>
    </Box>
);
